import {
    Card,
    CardActions,
    CardContent,
    Divider,
    Link as MuiLink,
    styled,
    SxProps,
    Theme,
    Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { ERole } from "../../models/enums/role";
import AuthService from "../../services/auth/AuthService";
import CloseButton from "../button/close/CloseButton";
import AccentDivider from "../divider/accentDivider/AccentDivider";

const Header = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0),
    margin: 0,
}));

const SubHeader = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing(0),
    marginBottom: 16,
}));

export const ModalRow = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing(0),
    marginBottom: 16,
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(0),
    fontWeight: theme.typography.fontWeightBold,
    width: 125,
}));

interface IHeaderTitleProps {
    title?: string;
    titleLink?: string;
    titleLinkState?: any;
    isTitleLinkExternal?: boolean;
}

const HeaderTitle = ({ title, titleLink, titleLinkState, isTitleLinkExternal }: IHeaderTitleProps) => {
    const userRole = AuthService.user?.role;

    if (userRole === ERole.Client || userRole === ERole.LimitedClient) {
        return (
            <Typography style={{ marginBottom: 4 }} variant="h6">
                {title}
            </Typography>
        );
    }

    return (
        titleLink ? (
            <MuiLink
                underline="hover"
                color="primary"
                sx={{ cursor: "pointer", textDecoration: "none" }}
            >
                <RouterLink
                    to={titleLink || ""}
                    state={titleLinkState}
                    target={isTitleLinkExternal ? "_blank" : undefined}
                    rel={isTitleLinkExternal ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                        if (!isTitleLinkExternal) return;

                        secureLocalStorage.setItem("rowParam", { ...titleLinkState });
                        e.stopPropagation();
                    }}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Typography style={{ marginBottom: 4 }} variant="h6">
                        {title}
                    </Typography>
                </RouterLink>
            </MuiLink>

        ) : (
            <Typography style={{ marginBottom: 4 }} variant="h6">
                {title}
            </Typography>
        )
    );
};

interface BaseModalProps {
    children?: ReactNode;
    label?: ReactNode;
    title?: string;
    titleLink?: string;
    titleLinkState?: any;
    isTitleLinkExternal?: boolean;
    subHeader?: ReactNode;
    closeCallback?: () => void;
    minWidth?: number | string;
    sx?: SxProps<Theme>;
}

const ModalCard = ({
    children,
    label,
    title,
    titleLink,
    titleLinkState,
    isTitleLinkExternal = false,
    subHeader,
    closeCallback,
    minWidth = 400,
    sx,
}: BaseModalProps) => {
    return (
        <Card sx={{ ...sx, px: 3, paddingTop: 3, minWidth }}>
            <CardContent sx={{ p: 0 }}>
                <header>
                    <Header>
                        {label ? (
                            <div>{label}</div>
                        ) : (
                            <HeaderTitle title={title} titleLink={titleLink} titleLinkState={titleLinkState} isTitleLinkExternal={isTitleLinkExternal} />
                        )}
                        {closeCallback && <CloseButton
                            onClick={closeCallback}
                            color="primary"
                        ></CloseButton>}
                    </Header>
                    {title && label && <HeaderTitle title={title} titleLink={titleLink} titleLinkState={titleLinkState} isTitleLinkExternal={isTitleLinkExternal} />}
                    {(title || subHeader) && <AccentDivider></AccentDivider>}
                    {subHeader && <SubHeader>{subHeader}</SubHeader>}
                </header>
                {children}
            </CardContent>
        </Card>
    );
};

interface BaseModalSectionProps {
    children?: ReactNode;
    isDivider?: boolean;
}

ModalCard.Main = ({ children, isDivider = true }: BaseModalSectionProps) => {
    return (
        <main>
            {isDivider && <Divider style={{ marginBottom: 16 }} />}
            {children}
        </main>
    );
};
ModalCard.Action = ({ children }: BaseModalSectionProps) => {
    return (
        <CardActions
            sx={{ display: "flex", justifyContent: "flex-end", padding: 0 }}
        >
            {children}
        </CardActions>
    );
};

export default ModalCard;
